import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import type { SxProps, Theme } from '@mui/material/styles'
import type { BellNotificationItem } from '../api/notifications-queries'
import { useMarkNotificationRead } from '../api/notifications-mutations'
import { formatNotificationTimeAgo } from '../lib/format-notification-time'
import { parseNotificationMessage } from '../lib/parse-notification-message'
import {
  notificationItemBorderColor,
  notificationItemUnreadBg,
} from '../styles/notificationPanelStyles'

const MONGO_OBJECT_ID_HEX24 = /^[a-fA-F0-9]{24}$/

function isValidMongoObjectIdString(id: string): boolean {
  return MONGO_OBJECT_ID_HEX24.test(id.trim())
}

function getNotificationCardSx(
  isUnread: boolean,
  isInteractive: boolean,
  compact: boolean,
): SxProps<Theme> {
  return {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
    px: '16px',
    py: '12px',
    m: 0,
    minHeight: compact ? 76 : 88,
    boxSizing: 'border-box',
    border: 'none',
    borderTop: `0.5px solid ${notificationItemBorderColor}`,
    borderBottom: `0.5px solid ${notificationItemBorderColor}`,
    borderRadius: 0,
    outline: 'none',
    appearance: 'none',
    bgcolor: isUnread ? notificationItemUnreadBg : '#FFFFFF',
    cursor: isInteractive ? 'pointer' : 'default',
    textAlign: 'left',
    font: 'inherit',
    color: 'inherit',
    '&:hover': isInteractive ? { bgcolor: isUnread ? '#E8F4FC' : '#F8FAFC' } : undefined,
    '&:focus-visible': {
      outline: '2px solid #008CD0',
      outlineOffset: -2,
    },
  }
}

type NotificationTitleRowProps = {
  compact: boolean
  displayTitle: string
  isUnread: boolean
  timeLabel: string
}

function NotificationTitleRow({
  compact,
  displayTitle,
  isUnread,
  timeLabel,
}: Readonly<NotificationTitleRowProps>) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
      {isUnread ? (
        <Box
          sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#008CD0', flexShrink: 0, mt: '6px' }}
          aria-hidden
        />
      ) : null}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '8px',
        }}
      >
        <Typography
          component="span"
          sx={{
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '20px',
            color: '#0F1729',
            overflow: compact ? 'hidden' : 'visible',
            textOverflow: compact ? 'ellipsis' : undefined,
            whiteSpace: compact ? 'nowrap' : 'normal',
            flex: 1,
            minWidth: 0,
          }}
        >
          {displayTitle}
        </Typography>
        {timeLabel ? (
          <Typography
            component="span"
            sx={{
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: '20px',
              color: '#6B7280',
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
          >
            {timeLabel}
          </Typography>
        ) : null}
      </Box>
    </Box>
  )
}

function NotificationDescription({
  compact,
  displayDescription,
}: Readonly<{ compact: boolean; displayDescription: string }>) {
  return (
    <Typography
      component="span"
      sx={{
        fontSize: compact ? '10px' : '12px',
        fontWeight: 400,
        lineHeight: compact ? '16px' : '18px',
        color: '#64748B',
        display: compact ? '-webkit-box' : 'block',
        WebkitLineClamp: compact ? 2 : undefined,
        WebkitBoxOrient: compact ? 'vertical' : undefined,
        overflow: compact ? 'hidden' : 'visible',
      }}
    >
      {displayDescription}
    </Typography>
  )
}

type NotificationItemContentProps = {
  compact: boolean
  displayTitle: string
  displayDescription: string | undefined
  isUnread: boolean
  timeLabel: string
}

function NotificationItemContent({
  compact,
  displayTitle,
  displayDescription,
  isUnread,
  timeLabel,
}: Readonly<NotificationItemContentProps>) {
  return (
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <NotificationTitleRow
        compact={compact}
        displayTitle={displayTitle}
        isUnread={isUnread}
        timeLabel={timeLabel}
      />
      {displayDescription ? (
        <NotificationDescription compact={compact} displayDescription={displayDescription} />
      ) : null}
    </Box>
  )
}

type NotificationItemProps = {
  notification: BellNotificationItem
  onNavigate: (url: string) => void
  compact?: boolean
}

export function NotificationItem({
  notification,
  onNavigate,
  compact = false,
}: Readonly<NotificationItemProps>) {
  const { mutate: markRead } = useMarkNotificationRead()
  const isUnread = !notification.isRead
  const { title, description } = parseNotificationMessage(notification.message)
  const displayTitle = notification.title ?? title
  const displayDescription = notification.description ?? description
  const timeLabel = formatNotificationTimeAgo(notification.createdAt)
  const hasNavigation = Boolean(notification.relatedUrl)
  const canMarkReadOnClick =
    isUnread && isValidMongoObjectIdString(notification.id)
  const isInteractive = hasNavigation || canMarkReadOnClick
  const cardSx = getNotificationCardSx(isUnread, isInteractive, compact)
  const testId = `provider-notification-item-${notification.id}`

  const content = (
    <NotificationItemContent
      compact={compact}
      displayTitle={displayTitle}
      displayDescription={displayDescription}
      isUnread={isUnread}
      timeLabel={timeLabel}
    />
  )

  const handleClick = () => {
    if (canMarkReadOnClick) {
      markRead(notification.id)
    }
    if (hasNavigation && notification.relatedUrl) {
      onNavigate(notification.relatedUrl)
    }
  }

  if (isInteractive) {
    return (
      <Box component="button" type="button" data-testid={testId} onClick={handleClick} sx={cardSx}>
        {content}
      </Box>
    )
  }

  return (
    <Box data-testid={testId} sx={cardSx}>
      {content}
    </Box>
  )
}
